import React, { useState, useEffect } from "react";
import "../styles/ManageInvoices.css";

function ManageInvoices() {
  const [representatives, setRepresentatives] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedRep, setSelectedRep] = useState(null);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Invoice form state
  const [clientNameAR, setClientNameAR] = useState("");
  const [clientNameEN, setClientNameEN] = useState("");
  const [notes, setNotes] = useState("");
  const [invoiceProducts, setInvoiceProducts] = useState([
    { product: "", soldQuantity: 1, price: 0, productName: "" },
  ]);

  // Fetch data on component mount
  useEffect(() => {
    fetchRepresentatives();
    fetchProducts();
  }, []);

  // Fetch invoice history when representative is selected
  useEffect(() => {
    if (selectedRep) {
      fetchInvoiceHistory(selectedRep._id);
    }
  }, [selectedRep]);

  const fetchRepresentatives = async () => {
    try {
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/representatives/getRepresentatives"
      );
      if (!response.ok) throw new Error("Failed to fetch representatives");
      const data = await response.json();
      setRepresentatives(data);
    } catch (error) {
      console.error("Error fetching representatives:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        "https://cairomed-backend.vercel.app/api/products/getProduct"
      );
      if (!response.ok) throw new Error("Failed to fetch products");
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchInvoiceHistory = async (repId) => {
    try {
      const response = await fetch(
        `https://cairomed-backend.vercel.app/api/invoices/representative/${repId}`
      );
      if (!response.ok) throw new Error("Failed to fetch invoice history");
      const data = await response.json();
      setInvoiceHistory(data.data.invoices || []);
    } catch (error) {
      console.error("Error fetching invoice history:", error);
    }
  };

  const handleRepSelect = (representative) => {
    setSelectedRep(representative);
    resetForm();
  };

  const handleBackClick = () => {
    setSelectedRep(null);
    resetForm();
  };

  const resetForm = () => {
    setClientNameAR("");
    setClientNameEN("");
    setNotes("");
    setInvoiceProducts([
      { product: "", soldQuantity: 1, price: 0, productName: "" },
    ]);
    setIsEditing(false);
    setSelectedInvoice(null);
    setShowModal(false);
  };

  const handleProductChange = (index, field, value, selectedProduct = null) => {
    const updatedProducts = [...invoiceProducts];

    if (field === "product") {
      const product = selectedProduct || products.find((p) => p._id === value);
      
      updatedProducts[index] = {
        ...updatedProducts[index],
        product: value,
        price: product?.Price || updatedProducts[index].price,
        productName: product
          ? `${product.ProductNameEN} - ${product.ProductNameAR}`
          : updatedProducts[index].productName || "Unknown Product"
      };
    } else {
      updatedProducts[index][field] = value;
    }

    setInvoiceProducts(updatedProducts);
  };

  const addProductField = () => {
    setInvoiceProducts([
      ...invoiceProducts,
      { product: "", soldQuantity: 1, price: 0, productName: "" },
    ]);
  };

  const removeProductField = (index) => {
    if (invoiceProducts.length > 1) {
      const updatedProducts = [...invoiceProducts];
      updatedProducts.splice(index, 1);
      setInvoiceProducts(updatedProducts);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedRep) {
      alert("Please select a representative first");
      return;
    }

    // Validate form
    if (!clientNameEN || !clientNameAR) {
      alert("Please enter client names in both languages");
      return;
    }

    for (const item of invoiceProducts) {
      if (!item.product || item.soldQuantity <= 0) {
        alert("Please fill all product fields correctly");
        return;
      }
    }

    try {
      let response;
      const invoiceData = {
        clientNameAR,
        clientNameEN,
        products: invoiceProducts.map((item) => ({
          product: item.product,
          soldQuantity: item.soldQuantity,
          price: item.price,
        })),
        representative: selectedRep._id,
        notes,
      };

      if (isEditing && selectedInvoice) {
        response = await fetch(
          `https://cairomed-backend.vercel.app/api/invoices/${selectedInvoice._id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(invoiceData),
          }
        );
      } else {
        response = await fetch(
          "https://cairomed-backend.vercel.app/api/invoices",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(invoiceData),
          }
        );
      }

      if (!response.ok)
        throw new Error(
          isEditing ? "Failed to update invoice" : "Failed to create invoice"
        );

      resetForm();
      fetchInvoiceHistory(selectedRep._id);

      alert(
        isEditing
          ? "Invoice updated successfully!"
          : "Invoice created successfully!"
      );
    } catch (error) {
      console.error("Error:", error);
      alert(
        isEditing ? "Failed to update invoice" : "Failed to create invoice"
      );
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        const response = await fetch(
          `https://cairomed-backend.vercel.app/api/invoices/${invoiceId}`,
          {
            method: "DELETE",
          }
        );

        if (!response.ok) throw new Error("Failed to delete invoice");

        fetchInvoiceHistory(selectedRep._id);
        alert("Invoice deleted successfully!");
      } catch (error) {
        console.error("Error deleting invoice:", error);
        alert("Failed to delete invoice");
      }
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const response = await fetch(
        `https://cairomed-backend.vercel.app/api/invoices/${invoiceId}`
      );
      if (!response.ok) throw new Error("Failed to fetch invoice details");
      const { data } = await response.json();
      
      const formattedInvoice = {
        ...data.invoice,
        products: data.invoice.products.map(item => {
          const product = item.product?._id 
            ? item.product 
            : products.find(p => p._id === item.product) || item.product;
          
          return {
            ...item,
            product: product,
            productName: product?.ProductNameEN 
              ? `${product.ProductNameEN} - ${product.ProductNameAR}` 
              : product?.productName || "Unknown Product"
          };
        })
      };
      
      setSelectedInvoice(formattedInvoice);
      setShowModal(true);
    } catch (error) {
      console.error("Error viewing invoice:", error);
      alert("Failed to load invoice details");
    }
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      const response = await fetch(
        `https://cairomed-backend.vercel.app/api/invoices/${invoiceId}`
      );
      if (!response.ok) throw new Error("Failed to fetch invoice details");

      const { data } = await response.json();
      if (!data?.invoice) throw new Error("Invalid invoice data");

      const invoice = data.invoice;

      // Set basic info
      setSelectedInvoice(invoice);
      setClientNameAR(invoice.clientNameAR || "");
      setClientNameEN(invoice.clientNameEN || "");
      setNotes(invoice.notes || "");

      // Handle products
      if (invoice.products?.length > 0) {
        const productsForForm = invoice.products.map((item) => {
          // First try to find exact match by ID in products list
          const exactMatch = products.find(p => p._id === (item.product?._id || item.product));
          
          // If no exact match, try to find by name (case insensitive)
          const nameMatch = !exactMatch && item.product?.ProductNameEN 
            ? products.find(p => 
                p.ProductNameEN.toLowerCase() === item.product.ProductNameEN.toLowerCase() &&
                p.ProductNameAR.toLowerCase() === item.product.ProductNameAR.toLowerCase()
              )
            : null;

          const matchedProduct = exactMatch || nameMatch || item.product || { _id: item.product };
          
          return {
            product: matchedProduct._id,
            soldQuantity: item.soldQuantity || 1,
            price: item.price || matchedProduct?.Price || 0,
            productName: matchedProduct?.ProductNameEN
              ? `${matchedProduct.ProductNameEN} - ${matchedProduct.ProductNameAR}`
              : item.product?.ProductNameEN
                ? `${item.product.ProductNameEN} - ${item.product.ProductNameAR}`
                : "Unknown Product"
          };
        });
        
        setInvoiceProducts(productsForForm);
      } else {
        setInvoiceProducts([
          { product: "", soldQuantity: 1, price: 0, productName: "" },
        ]);
      }

      setIsEditing(true);
      document.getElementById("invoice-form").scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      console.error("Error editing invoice:", error);
      alert(`Failed to load invoice for editing: ${error.message}`);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
  };

  return (
    <div className="manage-invoices-container" id="DashRepSelect">
      {!selectedRep ? (
        <div className="representative-container container mt-7">
          {representatives.map((representative) => {
            return (
              <div
                className="representative-card mb-3"
                key={representative._id}
                onClick={() => handleRepSelect(representative)}
              >
                <picture>
                  <img
                    src={representative.Image || "fallback-image.jpg"}
                    className="card-img-top cardImg"
                    alt={representative.NameEN || "Representative"}
                  />
                </picture>
                <div className="representative-card-content">
                  <div>
                    <i className="fa-regular fa-address-card cardText"></i>
                    <p>
                      <span>Name:</span>
                      {representative.NameEN || "N/A"}
                    </p>
                  </div>
                  <div>
                    <i className="fa-regular fa-address-card cardText"></i>
                    <p>
                      <span>Phone Number:</span>
                      {representative.PhoneNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <i className="fa-regular fa-address-card cardText"></i>
                    <p>
                      <span>Region:</span>
                      {representative.AreaEN || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="invoice-management">
          <div className="header d-flex justify-content-between align-items-center my-4">
            <h2>Manage Invoices for {selectedRep.NameEN}</h2>
            <button onClick={handleBackClick} className="btn btn-warning ">
              
              <span>Back </span>
            </button>
          </div>

          <div className="invoice-form-container container" id="invoice-form">
            <h3>{isEditing ? "Edit Invoice" : "Create New Invoice"}</h3>
            <form
              onSubmit={handleSubmit}
              className="row w-100 px-0 g-4 pt-3 pb-5"
            >
              <div className="col-lg-6 col-12 d-flex flex-column gap-2">
                <label>Client Name (Arabic)</label>
                <input
                  type="text"
                  value={clientNameAR}
                  onChange={(e) => setClientNameAR(e.target.value)}
                  required
                />
              </div>

              <div className="col-lg-6 col-12 d-flex flex-column gap-2">
                <label>Client Name (English)</label>
                <input
                  type="text"
                  value={clientNameEN}
                  onChange={(e) => setClientNameEN(e.target.value)}
                  required
                />
              </div>

              <div className="products-section">
                <h4>Products</h4>
                {invoiceProducts.map((item, index) => (
                  <div
                    key={index}
                    className="product-row d-flex flex-wrap mb-4"
                  >
                    <div className="col-12 d-flex flex-column gap-2 mb-2 px-2">
                      <label>Product</label>
                      <select
                        value={item.product || ""}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          const selectedProduct = products.find(p => p._id === selectedValue);
                          handleProductChange(
                            index,
                            "product",
                            selectedValue,
                            selectedProduct
                          );
                        }}
                        required
                      >
                        <option value="">Select Product</option>
                        {products.map((product) => (
                          <option 
                            key={product._id} 
                            value={product._id}
                            selected={item.product === product._id}
                          >
                            {product.ProductNameEN} - {product.ProductNameAR}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 col-12 d-flex flex-column gap-2 px-2">
                      <label>Quantity</label>
                      <input
                        type="number"
                        min="1"
                        value={item.soldQuantity}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "soldQuantity",
                            parseInt(e.target.value) || 0
                          )
                        }
                        required
                      />
                    </div>

                    <div className="col-md-6 col-12 d-flex flex-column gap-2 px-2">
                      <label>Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={(e) =>
                          handleProductChange(
                            index,
                            "price",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        required
                      />
                    </div>

                    {invoiceProducts.length > 1 && (
                      <button
                        type="button"
                        className="cancel-btn"
                        onClick={() => removeProductField(index)}
                      >
                        <span>Delete </span>
                        &times;
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="add-product submit-btn"
                  onClick={addProductField}
                >
                  <span>+ Add Another Product</span>
                </button>
              </div>

              <div className="col-12 d-flex flex-column gap-2">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="submit-btn">
                  {isEditing ? "Update Invoice" : "Create Invoice"}
                </button>
                {isEditing && (
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={resetForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="invoice-history">
            <h3>Invoice History</h3>
            {invoiceHistory.length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Client (EN)</th>
                      <th>Products</th>
                      <th>Total</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceHistory.map((invoice) => (
                      <tr key={invoice._id}>
                        <td>{invoice.clientNameEN}</td>
                        <td>
                          <ul>
                            {invoice.products.map((item, i) => {
                              const product = item.product?._id
                                ? item.product
                                : products.find((p) => p._id === item.product);
                              const name = product?.ProductNameEN
                                ? `${product.ProductNameEN} - ${product.ProductNameAR}`
                                : "Unknown Product";
                              return (
                                <li key={i}>
                                  {name}: {item.soldQuantity} × {item.price} EGP
                                </li>
                              );
                            })}
                          </ul>
                        </td>
                        <td>
                          {invoice.products.reduce(
                            (sum, item) => sum + item.soldQuantity * item.price,
                            0
                          )}{" "}
                          EGP
                        </td>
                        <td>
                          {new Date(invoice.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleViewInvoice(invoice._id)}
                            >
                              View
                            </button>
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleEditInvoice(invoice._id)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteInvoice(invoice._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No invoice history found for this representative.</p>
            )}
          </div>

          {showModal && selectedInvoice && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg">
                <div className="modal-content printable-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Invoice Details - {selectedInvoice.invoiceNumber}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p>
                          <strong>Client (EN):</strong>{" "}
                          {selectedInvoice.clientNameEN}
                        </p>
                        <p>
                          <strong>Client (AR):</strong>{" "}
                          {selectedInvoice.clientNameAR}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p>
                          <strong>Date:</strong>{" "}
                          {new Date(
                            selectedInvoice.createdAt
                          ).toLocaleDateString()}
                        </p>
                        <p>
                          <strong>Status:</strong> {selectedInvoice.status}
                        </p>
                      </div>
                    </div>

                    <h6>Products:</h6>
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Quantity</th>
                          <th>Price</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInvoice.products.map((item, i) => {
                          const product = item.product?._id
                            ? item.product
                            : products.find((p) => p._id === item.product);
                          const name = product?.ProductNameEN
                            ? `${product.ProductNameEN} - ${product.ProductNameAR}`
                            : item.productName || "Unknown Product";
                          return (
                            <tr key={i}>
                              <td>{name}</td>
                              <td>{item.soldQuantity}</td>
                              <td>{item.price} EGP</td>
                              <td>{item.soldQuantity * item.price} EGP</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="text-end">
                            <strong>Total:</strong>
                          </td>
                          <td>
                            <strong>
                              {selectedInvoice.totalInvoicePrice ||
                                selectedInvoice.products.reduce(
                                  (sum, item) =>
                                    sum + item.soldQuantity * item.price,
                                  0
                                )}{" "}
                              EGP
                            </strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>

                    {selectedInvoice.notes && (
                      <div className="mt-3">
                        <h6>Notes:</h6>
                        <p>{selectedInvoice.notes}</p>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => window.print()}
                    >
                      Print Invoice
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ManageInvoices;